import { useAppSelector } from 'hooks';
import dayjs from 'dayjs';
import {
  CARE_PLAN_CHECKLIST_LABELS,
  CarePlanChecklistItem,
  getCarePlanTypeConfig,
} from '~clients/config/carePlanTypes';

import { isBeforeDay } from '~libs/dayjs';

import { CarePlan } from '~clients/models';

import {
  useGetCarePlanCulturalSafetyQuery,
  useGetCarePlanGoalsQuery,
  useGetCarePlanNotificationsQuery,
  useGetCarePlanServiceLinesQuery,
} from '~clients/actions/carePlans';
import { useGetBudgetsQuery } from '~support-at-home/actions/budgets';

import { ClientPackageScheduleRow } from '~main/reducers/clients/clientPackageSchedules';

import {
  selectClientPackageSchedules,
  selectClientPackageSchedulesLoading,
} from '~main/selectors/clients/clientPackageSchedules';

export interface ChecklistItemState {
  key: CarePlanChecklistItem;
  label: string;
  passed: boolean;
}

export interface ActivationChecklist {
  items: ChecklistItemState[];
  completeCount: number;
  total: number;
  isLoading: boolean;
}

/**
 * Resolve the package schedule a plan is linked to. The plan stores the package
 * definition id (package_id), not a schedule id — the linked schedule is the
 * client's active schedule for that package (latest start date when renewals
 * overlap). The schedules slice is shared state, so rows are also filtered to
 * the plan's client to guard against a stale list from another page.
 */
export const findLinkedPackageSchedule = (
  schedules: ClientPackageScheduleRow[],
  plan: Pick<CarePlan, 'client_id' | 'package_id'>,
): ClientPackageScheduleRow | undefined =>
  schedules
    .filter((schedule) => schedule.client_id === plan.client_id && schedule.package.id === plan.package_id)
    .sort((a, b) => (a.start_date < b.start_date ? 1 : -1))[0];

/**
 * Derives the Overview-tab activation checklist from live plan data. Items are
 * read-only — each passes automatically once its underlying condition is met.
 * Expects the client's active package schedules to have been fetched into the
 * clientPackageSchedules slice (the Overview tab dispatches that fetch).
 */
export const useActivationChecklist = (plan: CarePlan): ActivationChecklist => {
  const { checklist } = getCarePlanTypeConfig(plan.plan_type);

  const schedules = useAppSelector(selectClientPackageSchedules);
  const schedulesLoading = useAppSelector(selectClientPackageSchedulesLoading);

  const goals = useGetCarePlanGoalsQuery({ planId: plan.id });
  const serviceLines = useGetCarePlanServiceLinesQuery({ planId: plan.id });
  const notifications = useGetCarePlanNotificationsQuery({ planId: plan.id });
  const culturalSafety = useGetCarePlanCulturalSafetyQuery(
    { planId: plan.id },
    { skip: !checklist.includes('cultural_safety_completed') },
  );
  // The SAH budget item passes once at least one client_budget is linked to this
  // plan (care_plan_id FK). Skipped for plan types that don't gate on it.
  const budgets = useGetBudgetsQuery(
    { client_id: plan.client_id },
    { skip: !checklist.includes('sah_budget_linked') },
  );

  // A plan with no goals must not tick this item vacuously — require at least one.
  const allGoalsHaveService =
    (goals.data?.length ?? 0) > 0 &&
    (goals.data ?? []).every((goal) => (serviceLines.data ?? []).some(({ goal_id }) => goal_id === goal.id));

  const passedByItem: Record<CarePlanChecklistItem, boolean> = {
    // Both dates set and the end date not in the past (ordering is already enforced on save).
    valid_dates: !!plan.start_date && !!plan.end_date && !isBeforeDay(plan.end_date, dayjs()),
    package_schedule_linked: findLinkedPackageSchedule(schedules, plan) !== undefined,
    sah_budget_linked: (budgets.data ?? []).some((budget) => budget.care_plan_id === plan.id),
    participant_copy_sent: (notifications.data?.length ?? 0) > 0,
    rights_explained: plan.is_rights_explained,
    service_agreement_signed: plan.is_service_agreement_signed,
    // Completed only once the required cultural-background field is populated —
    // matches the Cultural Safety tab's incomplete-alert condition.
    cultural_safety_completed: !!culturalSafety.data?.background_notes,
    all_goals_have_service: allGoalsHaveService,
  };

  const items = checklist.map((key) => ({
    key,
    label: CARE_PLAN_CHECKLIST_LABELS[key],
    passed: passedByItem[key],
  }));

  return {
    items,
    completeCount: items.filter(({ passed }) => passed).length,
    total: items.length,
    isLoading:
      schedulesLoading ||
      goals.isLoading ||
      serviceLines.isLoading ||
      notifications.isLoading ||
      culturalSafety.isLoading ||
      budgets.isLoading,
  };
};
